// imports for app window layout
import { useAuth0 } from '@auth0/auth0-react';
import { useCopilotReadable} from "@copilotkit/react-core";
import { useCopilotAction } from "@copilotkit/react-core"; 

import MainWindow from '../../app_layout/main_window/MainWindow';
import ChatWindow from '../../app_layout/chat_window/ChatWindow';
import SideBar from '../../app_layout/sidebar/SideBar';
import SideBarInactive from '../../app_layout/sidebar/SideBarInactive';
import APP_WINDOW_CONFIG from '../../app_layout/layout_config';

// imports relevant for HTTP calls
import DisplayException from '../../error_messages/DisplayException';
import CircleLoader from 'react-spinners/CircleLoader';
import {SERVER_URL, WEBSOCKET_URL} from '../../config_server_url';

// import user session information
import UserSessionContext from '../../user_session/context_user_session';
import { SetSessionInfo_OnDeleteCurrentProject } from '../../user_session/session_info_utils';

// imports this module specific
import '../../css_styles/button_styles.css'
import ProjectInformation from '../../create_new_project/ProjectInformation';
import ShowProjectInformation from './ShowProjectInformation';
import PaginatedTable from '../../tables/PaginatedTable';
import TeamTable from '../../create_new_project/TeamTable';
import InfoPopup from '../../popup_windows/InfoPopup';

import { useNavigate } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';

function PageProjectInformation() {

    // ------------------ Display HTTP/Server Exceptions ---------------------------------
    const [Exception, setException] = useState(false);
    const [ExceptionMessage, setExceptionMessage] = useState('');
    // This Component requires to fetches data on first load -> setIsloading(true);
    // This Component does not fetch data on first load -> setIsloading(false);
    const [isLoading, setIsLoading] = useState(false);

    // ------------------ Load User Session Information ---------------------------------
    const usr_session_ctx = useContext(UserSessionContext);
    const { user } = useAuth0();
    const [broadcast, setBroadcast] = useState([]); //get session notifications via web sockets
    const [onOkBroadcast, setOnOkBroadcast] = useState(null);
    // ------------------ Add User Session Websockets For Notification Events Across All Tenant (Workspace) Members -----------------------------

    useEffect(() => {
        // Create WebSocket connection`${SERVER_URL}/all_projects/load/`
        const ws = new WebSocket(`${WEBSOCKET_URL}/user_session/ws_project/${usr_session_ctx.uuid_tenant}/${user.sub}`);
        // Connection opened
        ws.onopen = () => {
        //   console.log(`WebSocket connection established for tenant ${usr_session_ctx.uuid_tenant}, user ${user.sub}`);
        };
        ws.onerror = (error) => {
            // console.error("WebSocket error observed:", error);
          };
        // Listen for messages
        ws.onmessage = (event) => {
        //   console.log('Message received:', event.data);
          let parser = event.data.split('__');
          let sender = parser[0]
          let type = parser[1].split('.')[1]
          let project_uuid = parser[2]
          if (type === 'ProjectDelete'){
            if(project_uuid === usr_session_ctx.session_info.uuid_current_project) {
                let new_broadcast = (<>{sender} has deleted <strong>your current project uuid:{project_uuid}</strong></>);
                usr_session_ctx.session_info = SetSessionInfo_OnDeleteCurrentProject(usr_session_ctx);
                handleUpdateUserSession()
                setBroadcast(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcast(() => () => {
                    setBroadcast([]); // Clear broadcast state
                    navigate('/');    // Navigate to home
                  });
            }
          }
          if (type === 'ProjectInfoUpdate'){
            let new_broadcast = (<>Project infomration has been changed by {sender}.</>);
            setBroadcast(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcast(() => () => {
                    setBroadcast([]); // Clear broadcast state
                    load_project_info();    // Navigate to home
                  });
          }
        };
        // Connection closed
        ws.onclose = () => {
        //   console.log(`WebSocket connection closed for tenant ${usr_session_ctx.uuid_tenant}, user ${user.sub}`);
        };
        // Clean up WebSocket connection when component unmounts
        return () => {
          ws.close();
        };
      }, [usr_session_ctx.uuid_tenant]); // Effect runs when tenant_id or user_id changes


      function handleUpdateUserSession() {
        setIsLoading(true);
        let session_update = {key: 'session_info', value: usr_session_ctx.session_info}
        fetch(`${SERVER_URL}/user_session/update_field/`, {method: 'PUT',
                                                           headers:{'Content-Type':'application/json',
                                                                   'auth0_user': user.sub,
                                                                   'uuid_tenant': usr_session_ctx.uuid_tenant }, 
                                                           body:JSON.stringify(session_update)
        })
        .then((res) => {
        if(!res.ok) {
            throw new Error(`HTTP error ${res.status}`);
        }
            return res.json();
        })
        .then((data) => {
            setIsLoading(false);
        })
            .catch((err) => {
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        });
   }





    // ------------------ General Window Layout -----------------------------
        
    const [canvasSize, setCanvasSize] = useState({width: 0, height: 0});
    const [layout, setLayout] = useState({sidebar:0, main: 0, chat: 0});
    const [toggleSideBar, setToggleSideBar] = useState(false);

    useEffect(() => {
        function handleResize() {
            let window_height = window.innerHeight;
            let window_width = window.innerWidth;
            let canvas_height = window_height - APP_WINDOW_CONFIG.OFFSET_HEADER_YPOS_PX;
            let sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_PX;
            let chat = (window_width - sidebar)*APP_WINDOW_CONFIG.CHAT_WIDTH_RELATIVE;
            chat = Math.max (chat, APP_WINDOW_CONFIG.MIN_CHAT_WIDTH_PX);
            let main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            setCanvasSize({width: window_width, height: canvas_height});
            // if currently the sidebar is not toggled, then resize the sidbar widow to toggle_size
            if(toggleSideBar){
                sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_COLLAPSED_PX;
                main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            }
            setLayout({sidebar:sidebar, main: main, chat: chat});
        }
        // Set initial height
        handleResize();
        // Add event listener to handle window resize
        window.addEventListener('resize', handleResize);
        // Remove event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [toggleSideBar]); // Empty dependency array ensures the effect only runs once


    function handleToggle() {
        // if currently the sidebar is not toggled, then resize the sidbar widow to toggle_size
        if(!toggleSideBar){
            let window_width = window.innerWidth;
            let sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_COLLAPSED_PX;
            let chat = layout.chat;
            let main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            setLayout({sidebar:sidebar, main: main, chat: chat});
        }
        else
        {
            let window_width = window.innerWidth;
            let sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_PX;
            let chat = layout.chat;
            let main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            setLayout({sidebar:sidebar, main: main, chat: chat});
        }
        setToggleSideBar(!toggleSideBar);
    }

// ------------------ Side Bar Content -----------------------------

    let sidebar = (<>
        <SideBar width={layout.sidebar} 
                 toggle={toggleSideBar} 
                 handle_toggle={handleToggle} 
                 active_link='project_information' >
        </SideBar>
    </>)

// ------------------ Main Window Content ----------------------------

    const navigate = useNavigate();
    const [currentProjectExists, setCurrentProjectExists] = useState(false)
    const [project, setProject] = useState({uuid:'', object_type:'', header:{title:'', descritpion:''}, team:[], uuids_all_opt_campaigns:[]})
    const [templateTeamMember, setTemplateTeamMember] = useState(null);
    const [edit, setEdit] = useState(false)


    /// CoPilotKit Hook
    useCopilotReadable({
        description: "Project Title",
        value: project.header.title,
      });
   
    useCopilotAction({
        name: "updateProjectTitle",
        description: "update Project Title.",
        parameters: [
          {
            name: "newTitle",
            type: "string",
            description: "Set new project titel: name",
          },
        ],
        handler: async ({ newTitle }) => {
            setProject((prevProject) => ({
                ...prevProject, // Keep the existing properties of the project
                header: {
                  ...prevProject.header, // Keep the existing properties of header
                  title: newTitle, // Update only the title
                },
              }));
        },
      });

      useCopilotAction({
        name: "saveProject",
        description: "Save the project.",
        parameters: [
          {
            name: "",
            type: "string",
            description: "Call function handleSave",
          },
        ],
        handler: async () => {
            handleSave();            
        },
      });

      useCopilotAction({
        name: "discardChanges",
        description: "discard changes in project.",
        parameters: [
          {
            name: "",
            type: "string",
            description: "Call function handleDiscard",
          },
        ],
        handler: async () => {
            handleDiscard();            
        },
      });




    function load_project_info(){
        if (usr_session_ctx.session_info.uuid_current_project !== '') {
            setCurrentProjectExists(true)
            setIsLoading(true);
            let prj_uuid = usr_session_ctx.session_info.uuid_current_project
            fetch(`${SERVER_URL}/current_project/load/`, {method: 'POST',
                                                            headers:{'Content-Type':'application/json',
                                                                     'uuid_tenant': usr_session_ctx.uuid_tenant } ,
                                                            body:JSON.stringify(prj_uuid)})
            .then((res) => {
                if(!res.ok) {
                    throw new Error(`HTTP error ${res.status}`);
                }
                return res.json();
            })
            .then((data) => {
                if(data.msg !== ''){
                    let new_broadcast = (<>Your current project does not exist anymore.</>);
                    usr_session_ctx.session_info = SetSessionInfo_OnDeleteCurrentProject(usr_session_ctx);
                    handleUpdateUserSession()
                    setBroadcast(prevBroadcast => [...prevBroadcast, new_broadcast]);
                    setOnOkBroadcast(() => () => {
                        setBroadcast([]); // Clear broadcast state
                        navigate('/');    // Navigate to home
                      });
                    setIsLoading(false); 
                }
                else {
                    setProject(data.data)
                    setIsLoading(false); 
                }
            })
            .catch((err) => {
                setException(true);
                setExceptionMessage(`HTTPException: err_msg=${err.message}`);
            });
        }
        else{
            setCurrentProjectExists(false);
        }
    }


    useEffect(() => {
        load_project_info()
    }, [usr_session_ctx.session_info.uuid_current_project]);



    function handleCreateProject() {
        document.getElementById('nav_bar_create_new_project').click();
        navigate('/create_new_project')
    }

    function handleLoadProject() {
        document.getElementById('nav_bar_all_projects').click();
        navigate('/all_projects')
    }


    function handleEditInfo(){
        setEdit(true)
        setIsLoading(true);
        // load template to create new team members
        fetch(`${SERVER_URL}/new_project/team_member_template/`, {method: 'GET',
                                                                  headers:{'Content-Type':'application/json'}
        })
        .then((res) => {
            if(!res.ok) {
                throw new Error(`HTTP error ${res.status}`);
            }
            return res.json();
        })
        .then((data) => {
            let team_row = {selected: false};
            // Copy existing key-value pairs to the new object
            for (let key in data.data) {
                if (data.data.hasOwnProperty(key)) {
                    team_row[key] = data.data[key];
                }
            }   
            setTemplateTeamMember(team_row);
            setIsLoading(false); 
        })
        .catch((err) => {
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        });

        // Add property selected to project.team -> needed to remove team members
        let team = project.team.map(team => {
            // Create a new object containing the merged properties
            let team_row = Object.assign({}, { selected: false }, team);
            return team_row;
        });
        const updatedProject = { ...project, team: team } 
        setProject(updatedProject)
    }

    function handleDiscard(){
        setEdit(false)
        setIsLoading(true);
            let prj_uuid = usr_session_ctx.session_info.uuid_current_project
            fetch(`${SERVER_URL}/current_project/load/`, {method: 'POST',
                                                            headers:{'Content-Type':'application/json',
                                                                     'uuid_tenant': usr_session_ctx.uuid_tenant } ,
                                                            body:JSON.stringify(prj_uuid)})
            .then((res) => {
                if(!res.ok) {
                    throw new Error(`HTTP error ${res.status}`);
                }
                return res.json();
            })
            .then((data) => {
                setProject(data.data)
                setIsLoading(false); 
            })
            .catch((err) => {
                setException(true);
                setExceptionMessage(`HTTPException: err_msg=${err.message}`);
            });
    }

    function handleSave(){
        setEdit(false)
        setIsLoading(true); 
        // remove the 'checked' key from allTeamMembers
        // This will create a deep copy of the data in allTeamMembers, aka stripping ['selected'] will not affect the original object 
        let team = JSON.parse(JSON.stringify(project.team));
        team.forEach(dict => {
                delete dict['selected'];
        });
        let updated_project = {uuid: usr_session_ctx.session_info.uuid_current_project,
                               header: project.header,
                               team: team }
        fetch(`${SERVER_URL}/current_project/update_projectinfo/`, {method: 'POST',
                                                            headers:{'Content-Type':'application/json',
                                                                     'auth0_user': user.sub,
                                                                     'uuid_tenant': usr_session_ctx.uuid_tenant} ,
                                                            body:JSON.stringify(updated_project)})
        .then((res) => {
            if(!res.ok) {
                throw new Error(`HTTP error ${res.status}`);
            }
            return res.json();
        })
        .then((data) => {
            setProject(data.data)
            setIsLoading(false); 
        })
        .catch((err) => {
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        });
    }

    function handleTitle(event) {
        event.preventDefault();
        let value = event.target.value;
        const updatedProject = { ...project, header: { ...project.header, title: value } };
        // Set the updated project state
        setProject(updatedProject);
    }

    function handleQillDescription(html) {
        const updatedProject = { ...project, header: { ...project.header, description: html } };
        setProject(updatedProject);
    }


    function handleNewTeamMember(){
        const updatedTeam = [...project.team, templateTeamMember];
        const updatedProject = { ...project, team: updatedTeam };
        setProject(updatedProject)
    }

   function handleRemoveTeamMember(){
        const team = [...project.team];
        const updatedTeam = team.filter((member) => !member.selected);
        const updatedProject = { ...project, team: updatedTeam };
        setProject(updatedProject)
   }

   function handleTeamInput(event)
    {
        let key_id = event.target.id.split('_');
        let key = key_id[0];
        let id = key_id[1];
        let value = event.target.value
        // for input checkbox, need to get property target.checked
        if (key === 'selected') 
        {
            value = event.target.checked;
        }
        const updatedTeam = [...project.team];
        const updatedMember = { ...updatedTeam[id] };
        updatedMember[key] = value;
        updatedTeam[id] = updatedMember;
        const updatedProject = { ...project, team: updatedTeam };
        setProject(updatedProject)
    }

    let mainWindow = (<></>);
    // Exection and Error Messeges Get Priority and Are Evaluated Before Anything els
    if(Exception){
        mainWindow = (<>
                <MainWindow width={layout.main} >
                    <DisplayException>
                        {ExceptionMessage}
                    </DisplayException>  
                </MainWindow>
            </>);
    }

    let project_info = (<></>)

    if (!isLoading && !Exception && !edit && project && broadcast.length === 0 && currentProjectExists) {
         project_info = (<>
                    <ShowProjectInformation projectInfo={{title: project.header.title, description: project.header.description} }/>
                    <div style={{position: 'relative', left:'3%', width:'94%'}}>
                        <h4 style={{padding:'0px', margin:'0px', 'marginTop':'25px', 'marginBottom':'10px'}}>Project Team</h4>
                        <PaginatedTable colNames={['First Name', 'Last Name', 'Role', 'Department']}
                            tableRows={project.team}
                            itemsPerPage={15}/>
                    </div>
                    <div style={{position: 'relative', marginTop:'30px', marginBottom:'20px', left:'3%', width:'94%'}}>
                        <button className={'actionButton'} onClick={handleEditInfo}>Edit Info</button>
                    </div>
                </>)
    }

    if (!isLoading && !Exception && edit && project && broadcast.length === 0 && currentProjectExists) {
        project_info = (<>
                <ProjectInformation projectInfo={project.header}
                                    handleTitle={handleTitle}
                                    handleDescription={handleQillDescription}/>
                <div style={{position: 'relative', left:'3%', width:'94%'}}>
                <h4 style={{padding:'0px', margin:'0px', 'marginTop':'25px', 'marginBottom':'10px'}}>Project Team</h4>
                </div>
                    <TeamTable onInput={handleTeamInput} 
                            onAddRow={handleNewTeamMember}
                            team={project.team}
                            colNames={['', 'First Name', 'Last Name', 'Role', 'Department']} 
                            actionPanel={[{title:'Remove', action:handleRemoveTeamMember}]}/>
                <div style={{position: 'relative', display:'flex', marginTop:'30px', marginBottom:'20px', left:'3%', width:'94%'}}>
                    <button className={'actionButton'} onClick={handleSave}>Save</button>
                    <button className={'actionButton'} onClick={handleDiscard}>Discard</button>
                </div>
            </>)
    }


    if (!isLoading && !Exception && !currentProjectExists && broadcast.length === 0) { 
        // Regular Content and Logic of Main Window Goes Here
        mainWindow = (<>
            <MainWindow width={layout.main} >
                <div style={{fontSize:'18px', textAlign:'center', height:'autofit', marginTop:'50px', width:'100%'}} >
                    There is currently no project in focus.
                
                <div style={{position: 'relative', display: 'flex', gap:'20px', justifyContent:'center', marginTop:'25px', left:'3%', width:'94%'}}>
                    <button className={'actionButton'} onClick={handleCreateProject}>Create Project</button>
                    <button className={'actionButton'} style={{marginRight:'0px'}} onClick={handleLoadProject}>Load Project</button>
                </div>       
                </div>         
            </MainWindow>
        </>);
        sidebar = (<>
            <SideBarInactive width={layout.sidebar} />
        </>)
    }

    if (!isLoading && !Exception && currentProjectExists && broadcast.length === 0 ) { 
        // Regular Content and Logic of Main Window Goes Here
        mainWindow = (<>
            <MainWindow width={layout.main} >
                {project_info}
            </MainWindow>
        </>);
    }

    if (!isLoading && !Exception && broadcast.length > 0){
        mainWindow = (<>
            <MainWindow width={layout.main} >
            <div style={{position: 'relative', left:'3%', width:'94%', height:'100%'}}>
                <InfoPopup ypos={'60px'} handleOk={onOkBroadcast} >
                    <div style={{fontWeight:'bold', textAlign:'left'}}>Session has been updated by another user:</div>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', textAlign: 'left' }}>
                        {broadcast.map((item, index) => (
                        <li key={index}>{item}</li>
                        ))}
                    </ul>
                </InfoPopup>
                </div>
            </MainWindow>
            </>);
    }


    if (isLoading && !Exception) {   
        mainWindow = (<>
                <MainWindow width={layout.main} >
                    <div style = {{position:'absolute', top:'5px', display: 'flex', justifyContent: 'center', alignItems: 'center',  width:'100%', height:'100%', border:'1px solid black'}}>
                        <CircleLoader loading={isLoading} size={40} color='var(--aixelo-green)' />
                    </div>
                </MainWindow>
            </>);
       }


// ------------------ Return All Components ----------------------------

    // Show Sidebar, MainWindow and ChatWindow
    return(
        <>
        <div style={{position:'relative', display:'grid', gridTemplateColumns: 'auto auto auto', overflow: 'hidden', left:`${APP_WINDOW_CONFIG.OFFSET_WINDOW_LEFT_PX}px`, top:`${APP_WINDOW_CONFIG.OFFSET_HEADER_YPOS_PX}px`, height:canvasSize.height, width:'100%'}}>
            {sidebar}
            {mainWindow}
            <ChatWindow width={layout.chat}/>
        </div>    
        </>
    );

}


export default PageProjectInformation;